import React, { useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import MuiDrawer, { drawerClasses } from '@mui/material/Drawer';
import LanguageSwitcher from '../LanguageSwitcher';
import SharedPageBackground from '../SharedPageBackground/SharedPageBackground';

const drawerWidth = 240;
const collapsedDrawerWidth = 88;

const Drawer = styled(MuiDrawer)({
  width: drawerWidth,
  flexShrink: 0,
  boxSizing: 'border-box',
  [`& .${drawerClasses.paper}`]: {
    width: drawerWidth,
    boxSizing: 'border-box',
  },
});

/**
 * DashboardLayout - 可复用的 Dashboard 布局组件
 * 包含 AppBar、Drawer 和主内容区域
 */
function DashboardLayout({
  drawerContent,
  appBarTitle,
  appBarActions,
  courseName,
  courseTermCode,
  children,
  mobileOpen,
  onDrawerToggle,
  onLogout
}) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', position: 'relative' }}>
      <SharedPageBackground />
      
      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* 移动端：顶部仅保留菜单按钮和语言切换 */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          minHeight: 48
        }}
      >
        <IconButton
          color="inherit"
          onClick={onDrawerToggle}
          sx={{ display: { xs: 'flex', md: 'none' } }}
          aria-label="打开菜单"
        >
          <MenuIcon />
        </IconButton>
        <Box sx={{ ml: 'auto' }}>
          <LanguageSwitcher />
        </Box>
      </Box>

      {/* Content Area - Sidebar + Main Content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Drawer */}
        <Box
          component="nav"
          sx={{ 
            width: { md: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth },
            flexShrink: { md: 0 },
            display: { xs: 'none', md: 'block' },
            transition: 'width 0.3s ease',
            position: 'relative',
            zIndex: 1200
          }}
        >
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            height: '100%',
            zIndex: 1200,
            '& .MuiDrawer-paper': {
              position: 'relative',
              height: '100%',
              borderRight: 'none',
              boxSizing: 'border-box',
              width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              transition: 'width 0.3s ease',
              overflow: 'visible',
              zIndex: 1200,
              bgcolor: 'transparent',
              boxShadow: 'none'
            }
          }}
          open
        >
          {React.cloneElement(drawerContent, { 
            collapsed: sidebarCollapsed,
            onToggleCollapse: () => setSidebarCollapsed(!sidebarCollapsed)
          })}
        </Drawer>
        </Box>

        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              bgcolor: 'transparent',
              boxShadow: 'none'
            }
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Main Content - zIndex 高于 header，滚动时卡片显示在 header 之上，不被遮挡 */}
        <Box
          component="main"
          sx={{
            flex: 1,
            p: 3,
            bgcolor: 'transparent',
            overflow: 'auto',
            ml: { md: 2 },
            mt: { md: 2 },
            mb: { md: 2 },
            minWidth: 0,
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'hidden',
            overflowY: 'auto',
            zIndex: 10,
            position: 'relative'
          }}
        >
          {children}
        </Box>
      </Box>
      </Box>
    </Box>
  );
}

export default DashboardLayout;
